import React, { useState, useEffect, useRef } from 'react';
import { ethers } from 'ethers';
import { NETWORK } from './config';
import ABI from './abi.json';
import Dashboard from './components/Dashboard';
import Investment from './components/Investment';
import Withdraw from './components/Withdraw';
import './App.css';

function App() {
  const [provider, setProvider] = useState(null);
  const [signer, setSigner] = useState(null);
  const [account, setAccount] = useState('');
  const [userId, setUserId] = useState(0);
  const [contract, setContract] = useState(null);
  const [tokenContract, setTokenContract] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [isConnecting, setIsConnecting] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  
  // Ref to track connection state for event handlers
  const isConnectingRef = useRef(false);
  const hasConnectedRef = useRef(false);

  const switchNetwork = async () => {
    if (!window.ethereum) return false;
    
    try {
      await window.ethereum.request({
        method: 'wallet_switchEthereumChain',
        params: [{ chainId: NETWORK.chainId }]
      });
      return true;
    } catch (switchError) {
      if (switchError.code === 4902) {
        try {
          await window.ethereum.request({
            method: 'wallet_addEthereumChain',
            params: [{
              chainId: NETWORK.chainId,
              chainName: NETWORK.chainName,
              nativeCurrency: NETWORK.nativeCurrency,
              rpcUrls: NETWORK.rpcUrls,
              blockExplorerUrls: NETWORK.blockExplorerUrls
            }]
          });
          return true;
        } catch (addError) {
          console.error('Failed to add network:', addError);
          return false;
        }
      }
      console.error('Failed to switch network:', switchError);
      return false;
    }
  };

  const connectWallet = async () => {
    if (isConnecting) return;
    setIsConnecting(true);
    isConnectingRef.current = true;
    setError('');
    
    try {
      if (!window.ethereum) {
        throw new Error('Please install a Web3 wallet like Trust Wallet or MetaMask');
      }

      const accounts = await window.ethereum.request({
        method: 'eth_requestAccounts'
      });

      if (!accounts || accounts.length === 0) {
        throw new Error('No accounts found');
      }

      const currentChainId = await window.ethereum.request({
        method: 'eth_chainId'
      });

      if (currentChainId !== NETWORK.chainId) {
        const switched = await switchNetwork();
        if (!switched) {
          throw new Error('Please switch to the correct network');
        }
        // Small delay to let the network switch stabilize
        await new Promise(resolve => setTimeout(resolve, 500));
      }

      const web3Provider = new ethers.providers.Web3Provider(window.ethereum, 'any');
      const web3Signer = web3Provider.getSigner();
      const userAddress = accounts[0];

      const mlmContract = new ethers.Contract(
        NETWORK.contractAddress,
        ABI.mlm,
        web3Signer
      );

      const erc20Contract = new ethers.Contract(
        NETWORK.tokenAddress,
        ABI.token,
        web3Signer
      );

      setProvider(web3Provider);
      setSigner(web3Signer);
      setAccount(userAddress);
      setContract(mlmContract);
      setTokenContract(erc20Contract);

      const uId = await mlmContract.addressToId(userAddress);
      setUserId(uId.toNumber());
      
      hasConnectedRef.current = true;

    } catch (err) {
      console.error('Connection error:', err);
      setError(err.message || 'Failed to connect wallet');
    } finally {
      setIsConnecting(false);
      isConnectingRef.current = false;
    }
  };

  const disconnectWallet = () => {
    setProvider(null);
    setSigner(null);
    setAccount('');
    setUserId(0);
    setContract(null);
    setTokenContract(null);
    setError('');
    setShowDropdown(false);
    hasConnectedRef.current = false;
  };

  useEffect(() => {
    if (window.ethereum) {
      const handleAccountsChanged = (accounts) => {
        if (accounts.length === 0) {
          disconnectWallet();
        } else if (accounts[0] !== account && hasConnectedRef.current) {
          window.location.reload();
        }
      };

      const handleChainChanged = () => {
        // Only reload if we're not in the middle of connecting
        // and if we've already established a connection
        if (!isConnectingRef.current && hasConnectedRef.current) {
          window.location.reload();
        }
      };

      window.ethereum.on('accountsChanged', handleAccountsChanged);
      window.ethereum.on('chainChanged', handleChainChanged);

      return () => {
        window.ethereum.removeListener('accountsChanged', handleAccountsChanged);
        window.ethereum.removeListener('chainChanged', handleChainChanged);
      };
    }
  }, [account]);

  useEffect(() => {
    const handleClickOutside = () => setShowDropdown(false);
    if (showDropdown) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [showDropdown]);

  const formatAddress = (addr) => {
    if (!addr) return '';
    return `${addr.slice(0, 6)}...${addr.slice(-4)}`;
  };

  const copyAddress = () => {
    navigator.clipboard.writeText(account);
    setShowDropdown(false);
  };

  return (
    <div className="app">
      <div className="grid-bg"></div>
      <div className="particles">
        {[...Array(20)].map((_, i) => (
          <div key={i} className="particle" style={{
            left: `${Math.random() * 100}%`,
            animationDelay: `${Math.random() * 5}s`,
            animationDuration: `${10 + Math.random() * 20}s`
          }}></div>
        ))}
      </div>

      <header className="header">
        <div className="logo">
          <img src="/logo.png" alt="Starbitz" className="logo-img" />
          <span className="logo-text">STARBITZ</span>
        </div>
        
        <div className="header-right">
          {account ? (
            <div className="wallet-dropdown" onClick={(e) => e.stopPropagation()}>
              <button 
                className="wallet-btn"
                onClick={() => setShowDropdown(!showDropdown)}
              >
                <span className="status-dot"></span>
                {formatAddress(account)}
              </button>
              {showDropdown && (
                <div className="dropdown-menu">
                  <div className="dropdown-item user-info">
                    <span className="label">User ID</span>
                    <span className="value">{userId || 'Not Registered'}</span>
                  </div>
                  <div className="dropdown-item" onClick={copyAddress}>
                    <span>📋 Copy Address</span>
                  </div>
                  <div className="dropdown-item disconnect" onClick={disconnectWallet}>
                    <span>🔌 Disconnect</span>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <button 
              className="connect-btn" 
              onClick={connectWallet}
              disabled={isConnecting}
            >
              {isConnecting ? 'Connecting...' : 'Connect'}
            </button>
          )}
        </div>
      </header>

      {error && (
        <div className="error-banner">
          {error}
          <button onClick={() => setError('')}>✕</button>
        </div>
      )}

      {!account ? (
        <div className="landing">
          <div className="landing-content">
            <div className="ai-orb">
              <div className="orb-core"></div>
              <div className="orb-ring ring1"></div>
              <div className="orb-ring ring2"></div>
              <div className="orb-ring ring3"></div>
            </div>
            <h1>AI-Powered <span className="gradient-text">Trading Network</span></h1>
            <p>Experience next-generation decentralized investment with quantum AI algorithms and neural binary network architecture</p>
            <button 
              className="cta-btn" 
              onClick={connectWallet}
              disabled={isConnecting}
            >
              <span className="btn-glow"></span>
              {isConnecting ? 'Initializing...' : 'Launch Platform'}
            </button>
          </div>
        </div>
      ) : (
        <main className="main">
          <Dashboard 
            contract={contract}
            userId={userId}
            account={account}
          />
          
          <div className="dual-section">
            <Investment 
              contract={contract}
              tokenContract={tokenContract}
              userId={userId}
              account={account}
              setUserId={setUserId}
              setError={setError}
            />
            
            <Withdraw 
              contract={contract}
              userId={userId}
              setError={setError}
            />
          </div>
        </main>
      )}
    </div>
  );
}

export default App;